import {
  ApolloClient,
  ApolloLink,
  FetchResult,
  HttpLink,
  InMemoryCache,
  gql,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';

import { GraphQLError } from 'graphql/error/GraphQLError';

const REFRESH_TOKEN = gql`
  mutation clientPortalRefreshToken($refreshToken: String!) {
    clientPortalRefreshToken(refreshToken: $refreshToken)
  }
`;

const apiUrl = import.meta.env.VITE_API_URL;

const isRefreshRequest = (operation) => {
  return operation.operationName === 'clientPortalRefreshToken';
};

const returnTokenDependingOnOperation = (operation) => {
  if (isRefreshRequest(operation)) {
    return localStorage.getItem('refreshToken') || '';
  }

  return localStorage.getItem('token') || '';
};

const httpLink = new HttpLink({
  uri: apiUrl + '/gql',
  credentials: 'include',
});

const authLink = setContext((operation, { headers }) => {
  if (typeof window !== 'undefined') {
    const token = returnTokenDependingOnOperation(operation);

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  }

  return {
    headers: {
      ...headers,
    },
  };
});

// if error code UNAUTHENTICATED redirect to login

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (
          err.message === 'token expired' ||
          err.extensions?.code === 'SUBREQUEST_HTTP_ERROR'
        ) {
          const observable = new Observable<FetchResult>((observer) => {
            // used an annonymous function for using an async function
            (async () => {
              try {
                const accessToken = await refreshToken();

                if (!accessToken) {
                  localStorage.clear();
                  throw new GraphQLError('Your session has expired');
                }

                // Retry the failed request
                const subscriber = {
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                };

                forward(operation).subscribe(subscriber);
              } catch (err) {
                observer.error(err);
              }
            })();
          });
          return observable;
        }

        if (
          err.message === 'Not authenticated' ||
          err.extensions?.code === 'UNAUTHENTICATED'
        ) {
          localStorage.clear();
          window.location.href = '/signin';
          return;
        }
      }
    }
  }
);

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([errorLink, authLink, httpLink]),
});

const refreshToken = async () => {
  try {
    const refreshResolverResponse: any = await client.mutate({
      mutation: REFRESH_TOKEN,
      variables: {
        refreshToken:
          typeof window !== 'undefined'
            ? localStorage.getItem('refreshToken')
            : '',
      },
    });

    const newToken = refreshResolverResponse.data?.refreshToken;
    localStorage.setItem('token', newToken || '');

    // Dispatch custom event to trigger immediate user data refetch
    if (typeof window !== 'undefined' && newToken) {
      window.dispatchEvent(new Event('authTokenChanged'));
    }

    return newToken || '';
  } catch (err) {
    localStorage.clear();
    throw err;
  }
};

export default client;
